import {
    ItemPayload,
    DataRecord,
    DataPayload,
    EntityState,
    ActionStatusPayload,
    ErrorPayload,
    EntityMode,
    KeyPayload,
    ReadStatusPayload,
    Entity
} from '@/Shared/Types';

import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';

/**
 * =====================================================
 * Entity Slice Factory
 * =====================================================
 *
 * Provides a reusable Redux slice for managing entities
 * with dual-mode support ('active' vs 'trash'). 
 *
 * Each slice handles:
 * - CRUD-like operations for items (`setItem`, `addList`, `setList`, `removeItem`)
 * - Mode toggling (`toggleMode`)
 * - Status tracking (`loading`, `idle`, `success`, `error`)
 * - UI read state (`setReadStatus`)
 * - Error handling per mode (`setError`)
 *
 * Design notes:
 * - Supports lazy initialization of modes
 * - All reducers are domain-agnostic and can be used for any entity type
 * - Provides a single source of truth for entity data, status, and timestamps
 * - Timestamp updates and read status resets are automatic on data mutations
 *
 * Author: Samano CASTRE
 * Date: January 2026
 */


/**
 * =====================================================
 * Initial Data Structure
 * =====================================================
 *
 * active: contains items, status, lastUpdatedAt, readStatus
 * mode  : the currently active mode ('active' | 'trash')
 */
const initialData = {
    active: {
        keyPairData: {},
        status: 'idle',
        readStatus: false,
        lastUpdatedAt: new Date().toISOString(),
    },
    mode : 'active'
} as EntityState<any>;


/**
 * =====================================================
 * createEntitySlice
 * =====================================================
 *
 * Factory function that builds a slice for a given entity.
 * Provides a standardized API for handling entity data in Redux.
 *
 * Responsibilities:
 * - Encapsulate all entity mutations per mode
 * - Keep timestamp, status, read state in sync
 * - Expose generic CRUD-like reducers usable across domains
 */
export function createEntitySlice<T extends Entity>(name: string) {

    return createSlice({
        name,
        initialState: initialData as EntityState<T>,

        /**
         * =====================================================
         * Reducers
         * =====================================================
         *
         * Core operations provided by the slice:
         * - Data manipulation: setItem, addList, setList, removeItem, removeList
         * - Mode operations: toggleMode
         * - Status operations: setStatus, setLoading, setIdle
         * - UI read state: setReadStatus
         * - Error handling: setError
         * - Full reset: clearData
         *
         * Mode resolution:
         * - action.payload.mode → state.mode → 'active' fallback
         */
        reducers: {

            // Insert or update a single entity in the appropriate mode
            setItem(state, action: PayloadAction<ItemPayload<T>>) {
                const { item, mode } = action.payload;
                

                const targetMode = mode ?? state.mode ?? 'active';
                state[targetMode] ??= {  keyPairData: {}};

                const target = state[targetMode]!; 

                target.keyPairData[item.id] = item as Draft<T>;
                target.status = 'success';
                target.error = undefined;
                target.lastUpdatedAt = new Date().toISOString();
                target.readStatus = false;
            },

            // Merge a list of entities into existing items, replacing duplicates
            addList(state, action: PayloadAction<DataPayload<T>>) {

                const { items, mode } = action.payload;
                if(items?.length === 0) {
                    return;
                }

                const targetMode = mode ?? state.mode ?? 'active';
                state[targetMode] ??= { keyPairData: {}};

                const target = state[targetMode]!; 

                target.error = undefined;
                target.status = 'success';
                target.lastUpdatedAt = new Date().toISOString();
                target.readStatus = false;

                const newKeyPairData = (items ?? []).reduce((acc, item) => {
                    acc[item.id] = item;
                    return acc;
                }, {} as DataRecord<T>);

                // Merge new items into the mode
                target.keyPairData = {
                    ...target.keyPairData,
                    ...newKeyPairData,
                } as DataRecord<Draft<T>>;
            },

            // Remove a specific item from a mode
            removeList(state, action: PayloadAction<DataPayload<T>>) {
                const { items, mode } = action.payload;
                
                if(items?.length === 0) {
                    return;
                }

                const targetMode = mode ?? state.mode ?? 'active';
                const target = state[targetMode]!; 
                
                if(target) {
                    let hasDeleted = false;
                    
                    (items ?? []).forEach(item =>  {
                        hasDeleted ??= !!target.keyPairData?.[item.id];
                        delete target.keyPairData?.[item.id];
                    });

                    if(hasDeleted) {
                        target.lastUpdatedAt = new Date().toISOString();
                    }
                }
            },

            // Replace the entire list of items for a mode
            setList(state, action: PayloadAction<DataPayload<T>>) {
                const { items, mode } = action.payload;

                const targetMode = mode ?? state.mode ?? 'active';

                state[targetMode] ??= { keyPairData: {}};

                const target = state[targetMode]!; 
                target.error = undefined;
                target.status = 'success';
                target.lastUpdatedAt = new Date().toISOString();
                target.readStatus = false;

                const keyPairData = (items ?? [])
                    .reduce(
                        (acc, item) => {
                            acc[item.id] = item;
                            return acc;
                        },
                        {} as DataRecord<T>
                    );

                target.keyPairData = keyPairData as DataRecord<Draft<T>>;
            },

            // Update status of a mode (success, error, loading, idle)
            setStatus(state, action: PayloadAction<ActionStatusPayload>) {
                const targetMode = action.payload.mode ?? state.mode ?? 'active';
                if (state[targetMode]) {
                    const target = state[targetMode]!; 
                    target.status = action.payload.status;
                }
            },

            setLoading(state, action: PayloadAction<EntityMode | undefined>) {
                const targetMode = action?.payload ?? state.mode ?? 'active';
                if (state[targetMode]) {
                    const target = state[targetMode]!; 
                    target.status = 'loading';
                }
            },

            setIdle(state, action: PayloadAction<EntityMode | undefined>) {
                const targetMode = action?.payload ?? state.mode ?? 'active';
                if (state[targetMode]) {
                    const target = state[targetMode]!; 
                    target.status = 'idle';
                }
            },

            // Mark a mode's data as read/unread in the UI
            setReadStatus(state, action: PayloadAction<ReadStatusPayload>) {
                const { mode, isRead } = action.payload;

                const targetMode = mode ?? state.mode ?? 'active';
                if (state[targetMode]) {
                    const target = state[targetMode]!; 
                    target.readStatus = isRead;
                }
            },

            toggleMode(state) {
                const curMode = state.mode;
                state.mode = curMode === 'active' ? 'trash' : 'active';
            },

            // Register an error for a mode and update its status
            setError(state, action: PayloadAction<ErrorPayload>) {
                const { mode, error } = action.payload;

                const targetMode = mode ?? state.mode ?? 'active';
                if (state[targetMode]) {
                    const target = state[targetMode]!; 
                    target.status = 'error';
                    target.error = error;
                }
            },

            // Remove a specific item from a mode
            removeItem(state, action: PayloadAction<KeyPayload>) {
                const { mode, key } = action.payload;
                //console.table({mode, key})

                const targetMode = mode ?? state.mode ?? 'active';
                const target = state[targetMode]!; 
                
                if(target?.keyPairData?.[key]) {
                    target.lastUpdatedAt = new Date().toISOString();
                    //console.table(target.keyPairData[key]);
                    delete target.keyPairData[key];
                }
            },

            // Reset the slice to its initial state, only 'active' initialized
            clearData() {
                return initialData as EntityState<T>;
            },
        }
    });
}

/**
 * =====================================================
 * makeEntitySlice
 * =====================================================
 *
 * Helper function to quickly create a fully-typed slice object.
 * Returns:
 * - reducer
 * - actions
 * - slice name
 *
 * Use this to register the slice in your Redux store.
 */
export function makeEntitySlice<EntityState>(name: string) {
    const slice = createEntitySlice<any>(name);
    return {
        reducer: slice.reducer,
        actions: slice.actions,
        name: slice.name,
    };
}